<template>
  <div class="content-with-spacing project-evidence-page">
    <div class="breadcrumb-section">
      <h1 v-if="projectDetails"><i class="fas fa-file-alt"></i> Evidence for {{ projectDetails.name }}</h1>
      <h1 v-else>Project Evidence</h1>
    </div>
    <project-evidence-competencies
    :competencies="projectCompetencies"
    :competency-descriptions-visible="competencyDescriptionsVisible"
    @toggle-descriptions="competencyDescriptionsVisible = !competencyDescriptionsVisible"
    />

    <project-evidence-table
    :programme-id="programmeId"
    :project-id="projectId"
    :evidence-data="projectEvidenceData"
    @update-evidence-data="getProjectEvidence"
    />
  </div>
</template>

<style scoped>
.project-evidence-page {
  margin-top: 10px;
}

.breadcrumb-section {
  margin-bottom: 30px;
}

.breadcrumb-section h1 {
  margin-bottom: 10px;
  color: var(--kate-type-primary);
}
</style>

<script>
import useAppProgrammeStore from '@stores/app-programme-store';
import PageReadyMixin from '@mixins/page-ready-mixin';
import ProjectEvidenceCompetencies from './project-evidence-competencies.vue';
import ProjectEvidenceTable from './project-evidence-table.vue';

export default {
  name: 'ProjectEvidenceOverview',
  mixins: [PageReadyMixin],
  components: {
    ProjectEvidenceCompetencies,
    ProjectEvidenceTable,
  },
  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
  },

  data() {
    const programmeStore = useAppProgrammeStore();
    return {
      programmeStore,
      competencyDescriptionsVisible: false,
      projectEvidenceReady: false,
      projectCompetenciesReady: false,
      projectDetailsReady: false,
      projectEvidenceData: [],
      projectCompetencies: [],
      projectDetails: {},
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getProjectDetails();
    this.getProjectCompetencies();
    this.getProjectEvidence();
  },

  watch: {
    projectDetails() {
      this.registerCrumbs();
    },
    programmeDetails() {
      this.registerCrumbs();
    },
  },

  computed: {
    ready() {
      return this.projectCompetenciesReady && this.projectEvidenceReady && this.projectDetailsReady;
    },
    programmeDetails() {
      return this.programmeStore.getProgramme(this.programmeId);
    },
  },

  methods: {

    registerCrumbs() {
      if (!this.projectDetails || !this.programmeDetails) {
        return;
      }

      this.$crumbs.register([
        {
          text: this.programmeDetails.name,
          path: {
            name: 'programme_page',
            params: { programmeId: this.programmeId },
          },
        },
        {
          text: 'Projects',
          path: {
            name: 'programme_projects',
            params: { programmeId: this.programmeId },
          },
        },
        {
          text: `Evidence for ${this.projectDetails.name}`,
          active: true,
        },
      ]);
    },

    getProjectDetails() {
      this.projectDetailsReady = false;
      this.$logger.info('Fetching project details', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}`)
        .then(response => {
          this.$logger.info('Successfully fetched project details', this.projectId);
          this.projectDetails = response.data || null;
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching project details', this.projectId, err);
          this.$ktoast.warning('Error fetching project details');
          this.projectDetails = null;
        }).then(() => {
          this.projectDetailsReady = true;
        });
    },

    getProjectCompetencies() {
      this.projectCompetenciesReady = false;
      this.$logger.info('Fetching all competencies for the project', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}/competencies`)
        .then(response => {
          this.$logger.info('Succefully fetched all competencies for the project', this.projectId);
          this.projectCompetencies = response.data || [];
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching competencies for project', this.projectId, err);
          this.$ktoast.warning('Error fetching competencies');
          this.projectCompetencies = [];
        }).then(() => {
          this.projectCompetenciesReady = true;
        });
    },

    getProjectEvidence() {
      this.projectEvidenceReady = false;
      this.$logger.info('Fetching all evidence for the project', this.projectId);
      this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}/evidence?programme_id=${this.programmeId}`)
        .then(response => {
          this.$logger.info('Succefully fetched all evidence for the project', this.projectId);
          this.projectEvidenceData = response.data || [];
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching evidence for project', this.projectId, err);
          this.$ktoast.warning('Error fetching evidence for project');
          this.projectEvidenceData = [];
        }).then(() => {
          this.projectEvidenceReady = true;
        });
    },
  },
};
</script>
